<template>
    <el-dialog
        :visible="visible"
        title="Chi tiết yêu cầu tuyển dụng"
        width="660px"
        custom-class="ro-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="close"
        @closed="onClosed"
    >
        <div v-if="order" class="ro-detail">
            <div class="ro-detail__head">
                <div>
                    <div class="ro-detail__code">
                        <span>Mã yêu cầu: #{{ order.id }}</span>
                        <span v-if="order.code_order" class="ro-detail__code-order">
                            · Mã đơn: <b>{{ order.code_order }}</b>
                        </span>
                    </div>
                    <div class="ro-detail__title">{{ order.title || order.position_title || "Yêu cầu tuyển dụng" }}</div>
                </div>
                <div class="ro-detail__tags">
                    <order-type-tag :order="order" />
                    <order-status-tag :status="order.status" :status-label="order.status_label" />
                </div>
            </div>

            <div class="ro-detail__grid">
                <div v-if="order.code_order">
                    <span class="ro-detail__label">Mã đơn</span>
                    <span class="font-bold text-warning"><i class="el-icon-tickets"></i> {{ order.code_order }}</span>
                </div>
                <div>
                    <span class="ro-detail__label">Phòng ban</span>
                    <span>{{ departmentName }}</span>
                </div>
                <div>
                    <span class="ro-detail__label">Người tạo</span>
                    <span>{{ creatorName }} · {{ formatDate(order.created_at) }}</span>
                </div>
                <div v-if="order.recruitment_job_detail_name || order.job_detail || order.job_detail_name">
                    <span class="ro-detail__label">Job chi tiết</span>
                    <span class="font-bold text-primary">{{ order.recruitment_job_detail_name || order.job_detail || order.job_detail_name }}</span>
                </div>
                <div v-if="order.quantity || order.amount">
                    <span class="ro-detail__label">Số lượng tuyển</span>
                    <span class="font-bold">{{ order.quantity || order.amount }} nhân sự</span>
                </div>
                <div v-if="areaLabel">
                    <span class="ro-detail__label">Khu vực</span>
                    <span class="font-bold"><i class="el-icon-map-location"></i> {{ areaLabel }}</span>
                </div>
                <div v-if="order.branch_name || (order.branch && (order.branch.name || order.branch.branch_name)) || order.work_location || order.location">
                    <span class="ro-detail__label">Địa điểm làm việc</span>
                    <span><i class="el-icon-location-outline"></i> {{ order.branch_name || (order.branch && (order.branch.name || order.branch.branch_name)) || order.work_location || order.location }}</span>
                </div>
                <div v-if="workTypeLabel">
                    <span class="ro-detail__label">Hình thức làm việc</span>
                    <span><i class="el-icon-suitcase"></i> {{ workTypeLabel }}</span>
                </div>
                <div v-if="order.start_date || order.start_time || order.end_date || order.end_time">
                    <span class="ro-detail__label">Thời gian tuyển</span>
                    <span>
                        {{ formatDay(order.start_date || order.start_time) || "—" }}
                        <span v-if="order.end_date || order.end_time">
                            đến {{ formatDay(order.end_date || order.end_time) }}
                        </span>
                    </span>
                </div>
                <div v-if="order.hiring_start_date">
                    <span class="ro-detail__label">Bắt đầu chạy job</span>
                    <span class="text-primary font-weight-bold">
                        <i class="el-icon-date"></i> {{ formatDay(order.hiring_start_date) }}
                    </span>
                </div>
                <div v-if="remainingDaysInfo">
                    <span class="ro-detail__label">Số ngày tuyển còn lại</span>
                    <span class="ro-remaining-badge" :class="`ro-remaining-badge--${remainingDaysInfo.type}`">
                        <i :class="remainingDaysInfo.icon"></i> {{ remainingDaysInfo.text }}
                    </span>
                </div>
                <div v-if="showApprovalLevel && isTeacherOrder">
                    <span class="ro-detail__label">HR duyệt</span>
                    <span :class="approveClass(order.hr_approve)">
                        <i :class="approveIcon(order.hr_approve)"></i>
                        {{ order.hr_approve_label || approveText(order.hr_approve) }}
                    </span>
                </div>
                <div v-if="showApprovalLevel && isTeacherOrder">
                    <span class="ro-detail__label">TL duyệt</span>
                    <span :class="approveClass(order.teamlead_approve)">
                        <i :class="approveIcon(order.teamlead_approve)"></i>
                        {{ order.teamlead_approve_label || approveText(order.teamlead_approve) }}
                    </span>
                </div>
                <div>
                    <span class="ro-detail__label">Người phụ trách</span>
                    <div class="ro-detail__pic-row">
                        <span v-if="personInChargeName" class="ro-detail__pic-val">
                            <i class="el-icon-user-solid"></i> {{ personInChargeName }}
                        </span>
                        <span v-else class="ro-detail__pic-empty">Chưa phân công</span>
                        <el-button
                            v-if="canAssignPic && isApproved"
                            type="text"
                            size="mini"
                            class="ro-detail__pic-btn"
                            @click="handleAssign"
                        >
                            {{ personInChargeName ? "Đổi" : "Gán" }}
                        </el-button>
                    </div>
                </div>
                <div v-if="coordinatorName">
                    <span class="ro-detail__label">Người điều phối</span>
                    <div class="ro-detail__pic-row">
                        <span class="ro-detail__pic-val">
                            <i class="el-icon-connection"></i> {{ coordinatorName }}
                        </span>
                    </div>
                </div>
                <div v-if="isTeacherOrder">
                    <span class="ro-detail__label">Người duyệt CV</span>
                    <div class="ro-detail__pic-row">
                        <span v-if="cvApproverName" class="ro-detail__pic-val">
                            <i class="el-icon-check"></i> {{ cvApproverName }}
                        </span>
                        <span v-else class="ro-detail__pic-empty">Chưa gán</span>
                        <el-button
                            v-if="canAssignCvApprover && !isRejected"
                            type="text"
                            size="mini"
                            class="ro-detail__pic-btn"
                            @click="handleAssignCvApprover"
                        >
                            {{ cvApproverName ? "Đổi" : "Gán" }}
                        </el-button>
                    </div>
                </div>
                <div v-if="order.deadline">
                    <span class="ro-detail__label">Hạn xử lý</span>
                    <span>{{ formatDay(order.deadline) }}</span>
                </div>
            </div>

            <div v-if="order.description" class="ro-detail__block">
                <div class="ro-detail__block-title"><i class="el-icon-document"></i> Mô tả yêu cầu tuyển dụng</div>
                <div class="ro-detail__content">{{ order.description }}</div>
            </div>

            <div v-if="order.requirements" class="ro-detail__block">
                <div class="ro-detail__block-title"><i class="el-icon-finished"></i> Yêu cầu ứng viên</div>
                <div class="ro-detail__content">{{ order.requirements }}</div>
            </div>

            <div v-if="order.hr_reason_reject" class="ro-detail__block ro-detail__block--danger">
                <div class="ro-detail__block-title"><i class="el-icon-warning"></i> Lý do HR từ chối</div>
                <div class="ro-detail__content">{{ order.hr_reason_reject }}</div>
            </div>

            <div v-if="order.team_lead_reason_reject" class="ro-detail__block ro-detail__block--danger">
                <div class="ro-detail__block-title"><i class="el-icon-warning"></i> Lý do Teamlead từ chối</div>
                <div class="ro-detail__content">{{ order.team_lead_reason_reject }}</div>
            </div>

            <div v-if="order.reject_reason && !order.hr_reason_reject && !order.team_lead_reason_reject" class="ro-detail__block ro-detail__block--danger">
                <div class="ro-detail__block-title"><i class="el-icon-warning"></i> Lý do từ chối</div>
                <div class="ro-detail__content">{{ order.reject_reason }}</div>
            </div>
        </div>

        <span slot="footer">
            <!-- HR Duyệt & Từ chối (chỉ account_type_id = 16 & department_id = 3) -->
            <template v-if="canApproveHr">
                <el-button
                    v-if="order && order.hr_approve != 1"
                    size="small"
                    type="success"
                    icon="el-icon-check"
                    :loading="submitting"
                    @click="handleApprove('hr', 1)"
                >
                    HR Duyệt
                </el-button>
                <el-button
                    v-if="order && order.hr_approve != 2"
                    size="small"
                    type="danger"
                    plain
                    icon="el-icon-close"
                    :loading="submitting"
                    @click="handleApprove('hr', 2)"
                >
                    HR Từ chối
                </el-button>
            </template>

            <!-- Teamlead Duyệt & Từ chối (chỉ account_type_id = 16 & department_id = 52) -->
            <template v-if="canApproveTeamlead">
                <el-button
                    v-if="order && order.teamlead_approve != 1"
                    size="small"
                    type="success"
                    icon="el-icon-check"
                    :loading="submitting"
                    @click="handleApprove('teamlead', 1)"
                >
                    Teamlead Duyệt
                </el-button>
                <el-button
                    v-if="order && order.teamlead_approve != 2"
                    size="small"
                    type="danger"
                    plain
                    icon="el-icon-close"
                    :loading="submitting"
                    @click="handleApprove('teamlead', 2)"
                >
                    Teamlead Từ chối
                </el-button>
            </template>

            <el-button
                size="small"
                type="success"
                plain
                icon="el-icon-document-copy"
                @click="goToCvs"
            >
                Xem ứng viên ({{ cvCount }})
            </el-button>
            <el-button
                v-if="isApproved"
                size="small"
                type="warning"
                plain
                icon="el-icon-user"
                @click="handleAssign"
            >
                Gán phụ trách
            </el-button>
            <el-button
                v-if="canAssignCvApprover && isTeacherOrder && !isRejected"
                size="small"
                type="info"
                plain
                icon="el-icon-check"
                @click="handleAssignCvApprover"
            >
                Gán duyệt CV
            </el-button>
            <el-button size="small" type="primary" plain icon="el-icon-edit" @click="handleEdit">Chỉnh sửa</el-button>
            <el-button size="small" @click="close">Đóng</el-button>
        </span>
    </el-dialog>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters } from "vuex";
import {
    TYPE_TUYEN_HO,
    orderTypeOf,
    TYPE_APPROVE,
    DA_DUYET,
    TU_CHOI,
    isOrderApproved,
    canUserApproveHr,
    canUserApproveTeamlead,
    isOrderRejected,
    STATUS_KET_THUC,
    COORDINATOR_USER_ID,
    COORDINATOR_NAME,
    getRecruitmentAreaLabel,
} from "@/core/config/recruitmentOrders/recruitmentOrdersOption";
import OrderStatusTag from "./OrderStatusTag.vue";
import OrderTypeTag from "./OrderTypeTag.vue";

export default {
    name: "OrderDetailDialog",
    components: { OrderStatusTag, OrderTypeTag },
    props: {
        visible: { type: Boolean, default: false },
        order: { type: Object, default: null },
        currentUserId: { type: [Number, String], default: null },
        currentUser: { type: Object, default: null },
        isCoordinator: { type: Boolean, default: false },
        canAssignCvApprover: { type: Boolean, default: false },
        /** Chỉ account_type_id = 16 của department_id = 3 mới được gán HR phụ trách */
        canAssignPersonInCharge: { type: Boolean, default: false },
        showRemainingDays: { type: Boolean, default: false },
        showApprovalLevel: { type: Boolean, default: true },
        submitting: { type: Boolean, default: false },
    },
    computed: {
        isTeacherOrder() {
            return this.order && orderTypeOf(this.order) === TYPE_TUYEN_HO;
        },
        creatorName() {
            if (!this.order) return "—";
            if (this.order.created_user_info && this.order.created_user_info.name) return this.order.created_user_info.name;
            if (this.order.created_by_name) return this.order.created_by_name;
            if (this.order.created_user) return `ID ${this.order.created_user}`;
            return "—";
        },
        departmentName() {
            if (!this.order) return "—";
            if (this.order.department && this.order.department.name) return this.order.department.name;
            if (this.order.department_name) return this.order.department_name;
            return "—";
        },
        personInChargeName() {
            if (!this.order) return "";
            if (this.order.person_in_charge_id == null && !this.order.person_in_charge) return "";
            if (this.order.person_in_charge && this.order.person_in_charge.name) {
                if (!this.order.person_in_charge_id || String(this.order.person_in_charge.id) === String(this.order.person_in_charge_id)) {
                    return this.order.person_in_charge.name;
                }
            }
            if (this.order.person_in_charge_name) return this.order.person_in_charge_name;
            if (this.order.assigned_hr_teamlead_name) return this.order.assigned_hr_teamlead_name;
            if (this.order.person_in_charge_id) return `ID #${this.order.person_in_charge_id}`;
            return "";
        },
        cvApproverName() {
            if (!this.order) return "";
            if (this.order.cv_approver_id == null && !this.order.cv_approver) return "";
            if (this.order.cv_approver && this.order.cv_approver.name) {
                if (!this.order.cv_approver_id || String(this.order.cv_approver.id) === String(this.order.cv_approver_id)) {
                    return this.order.cv_approver.name;
                }
            }
            if (this.order.cv_approver_name) return this.order.cv_approver_name;
            if (this.order.cv_approver_id) {
                if (String(this.order.cv_approver_id) === String(COORDINATOR_USER_ID)) {
                    return "Ms. Cẩm Tú";
                }
                return `ID #${this.order.cv_approver_id}`;
            }
            return "";
        },
        coordinatorName() {
            if (!this.order) return "";
            if (this.order.coordinator && this.order.coordinator.name) return this.order.coordinator.name;
            if (this.order.coordinator_name) return this.order.coordinator_name;
            if (this.order.coordinator_id) {
                if (String(this.order.coordinator_id) === String(COORDINATOR_USER_ID)) return "Chị Cẩm Tú";
                return `ID #${this.order.coordinator_id}`;
            }
            return "";
        },
        canAssignPic() {
            return (
                this.canAssignPersonInCharge ||
                this.isCoordinator ||
                (this.order && this.order.coordinator_id && String(this.order.coordinator_id) === String(this.currentUserId))
            );
        },
        cvCount() {
            return (this.order && Array.isArray(this.order.cvs)) ? this.order.cvs.length : 0;
        },
        isApproved() {
            return isOrderApproved(this.order);
        },
        remainingDaysInfo() {
            if (!this.order) return null;
            const end = this.order.end_date || this.order.end_time || this.order.deadline;
            if (!end) {
                return { text: "Không giới hạn", type: "normal", icon: "el-icon-date" };
            }
            const endMoment = moment(end).isValid() ? moment(end).startOf("day") : moment(end, "YYYY-MM-DD").startOf("day");
            if (!endMoment.isValid()) {
                return { text: "Không giới hạn", type: "normal", icon: "el-icon-date" };
            }
            const today = moment().startOf("day");
            const diffDays = endMoment.diff(today, "days");

            if (diffDays > 3) {
                return { text: `Còn ${diffDays} ngày`, type: "safe", icon: "el-icon-time" };
            } else if (diffDays >= 1) {
                return { text: `Còn ${diffDays} ngày`, type: "warning", icon: "el-icon-warning-outline" };
            } else if (diffDays === 0) {
                return { text: "Hôm nay hết hạn", type: "danger", icon: "el-icon-alarm-clock" };
            } else {
                if (Number(this.order && this.order.status) === STATUS_KET_THUC) {
                    return { text: "Đã kết thúc", type: "normal", icon: "el-icon-circle-check" };
                }
                if (isOrderRejected(this.order)) {
                    return { text: "Đã từ chối", type: "normal", icon: "el-icon-circle-close" };
                }
                const past = Math.abs(diffDays);
                return { text: `Quá hạn ${past} ngày`, type: "danger", icon: "el-icon-circle-close" };
            }
        },
        canApproveHr() {
            return canUserApproveHr(this.currentUser);
        },
        canApproveTeamlead() {
            return canUserApproveTeamlead(this.currentUser);
        },
        isRejected() {
            return isOrderRejected(this.order);
        },
        ...mapGetters(["roWorkForms"]),
        workFormsMap() {
            const map = {};
            (this.roWorkForms || []).forEach((w) => {
                if (w && w.id !== undefined && w.id !== null) {
                    map[w.id] = w;
                }
            });
            return map;
        },
        workTypeLabel() {
            if (!this.order) return "";
            if (this.order.work_form && this.order.work_form.name) return this.order.work_form.name;
            if (this.order.work_type_name) return this.order.work_type_name;
            if (this.order.working_form_name) return this.order.working_form_name;
            const val = this.order.work_type !== undefined && this.order.work_type !== null && this.order.work_type !== ""
                ? this.order.work_type
                : (this.order.working_form !== undefined && this.order.working_form !== null && this.order.working_form !== "" ? this.order.working_form : null);
            if (val === null || val === undefined || val === "") return "";
            if (this.workFormsMap && this.workFormsMap[val]) {
                return this.workFormsMap[val].name;
            }
            if (val === 0 || val === "0" || String(val).toLowerCase() === "online") return "Online";
            if (val === 1 || val === "1" || String(val).toLowerCase() === "offline") return "Offline";
            return String(val);
        },
        areaLabel() {
            if (!this.order) return "";
            const val = this.order.area !== undefined && this.order.area !== null && this.order.area !== ""
                ? this.order.area
                : (this.order.area_id !== undefined && this.order.area_id !== null && this.order.area_id !== "" ? this.order.area_id : null);
            if (val === null || val === undefined || val === "") return "";
            return getRecruitmentAreaLabel(val);
        },
    },
    methods: {
        goToCvs() {
            if (!this.order || !this.order.id) return;
            const rJobId = (this.order.recruitment_job_id !== undefined && this.order.recruitment_job_id !== null && this.order.recruitment_job_id !== "")
                ? this.order.recruitment_job_id
                : this.order.id;
            const orderId = this.order.id;
            this.close();
            this.$router.push({
                path: "/recruitment-cvs",
                query: {
                    job_id: String(rJobId),
                    recruitment_job_id: String(rJobId),
                    order_id: String(orderId),
                },
            }).catch(() => {});
        },
        approveText(val) {
            return TYPE_APPROVE[val] || "Chờ duyệt";
        },
        approveClass(val) {
            const num = Number(val);
            if (num === DA_DUYET) return "ro-detail__val--success";
            if (num === TU_CHOI) return "ro-detail__val--danger";
            return "ro-detail__val--warning";
        },
        approveIcon(val) {
            const num = Number(val);
            if (num === DA_DUYET) return "el-icon-circle-check";
            if (num === TU_CHOI) return "el-icon-circle-close";
            return "el-icon-time";
        },
        formatDate(v) {
            return v ? moment(v).format("HH:mm DD/MM/YYYY") : "";
        },
        formatDay(v) {
            return v ? moment(v).format("DD/MM/YYYY") : "—";
        },
        close() {
            this.$emit("update:visible", false);
        },
        onClosed() {},
        handleApprove(role, status) {
            this.$emit("approve-order", { order: this.order, role, status });
            this.$emit("teamlead-approve", { order: this.order, role, status, statusVal: status });
        },
        handleAssign() {
            if (!this.canAssignPersonInCharge) {
                this.$message.warning("Chỉ account_type_id = 16 của department_id = 3 mới được gán HR phụ trách");
                return;
            }
            if (!this.isApproved) {
                this.$message.warning("Chỉ yêu cầu tuyển dụng ở trạng thái Đã duyệt mới được gán người phụ trách");
                return;
            }
            const current = this.order;
            this.close();
            this.$emit("assign", current);
        },
        handleAssignCvApprover() {
            if (this.isRejected) {
                this.$message.warning("Yêu cầu tuyển dụng đã bị từ chối, không thể gán người duyệt CV");
                return;
            }
            if (!this.isTeacherOrder) {
                this.$message.warning("Chỉ yêu cầu tuyển hộ mới được gán người duyệt CV");
                return;
            }
            if (!this.canAssignCvApprover) {
                this.$message.warning("Chỉ nhân sự thuộc Phòng Nhân sự mới được gán người duyệt CV");
                return;
            }
            const current = this.order;
            this.close();
            this.$emit("assign-cv-approver", current);
        },
        handleEdit() {
            const current = this.order;
            this.close();
            this.$emit("edit", current);
        },
    },
};
</script>

<style scoped>
.ro-detail__pic-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.ro-detail__pic-val {
    font-weight: 600;
    color: #1e293b;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.ro-detail__pic-val i {
    color: #4f46e5;
}
.ro-detail__pic-empty {
    color: #94a3b8;
    font-style: italic;
}
.ro-detail__pic-btn {
    padding: 0 !important;
    font-size: 12px;
    font-weight: 600;
}
.ro-detail__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f2f5;
}
.ro-detail__code {
    font-size: 12px;
    color: #4f46e5;
    font-weight: 700;
    margin-bottom: 4px;
}
.ro-detail__code-order {
    color: #b45309;
    margin-left: 6px;
}
.ro-detail__code-order b {
    color: #92400e;
    font-weight: 700;
}
.ro-detail__title {
    font-size: 16px;
    font-weight: 700;
    color: #1f2430;
    line-height: 1.4;
}
.ro-detail__tags {
    display: flex;
    align-items: center;
    gap: 8px;
}
.ro-detail__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 20px;
    background: #f8fafc;
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 18px;
    font-size: 13px;
}
.ro-detail__grid > div {
    display: flex;
    flex-direction: column;
    gap: 3px;
}
.ro-detail__label {
    font-size: 11.5px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
}
.ro-detail__val--success {
    color: #059669;
    font-weight: 600;
}
.ro-detail__val--danger {
    color: #dc2626;
    font-weight: 600;
}
.ro-detail__val--warning {
    color: #d97706;
    font-weight: 600;
}
.ro-detail__block {
    margin-bottom: 16px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px 16px;
}
.ro-detail__block--danger {
    border-color: #fecaca;
    background: #fef2f2;
}
.ro-detail__block-title {
    font-weight: 700;
    font-size: 13px;
    color: #334155;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
}
.ro-detail__block--danger .ro-detail__block-title {
    color: #dc2626;
}
.ro-detail__content {
    font-size: 13.5px;
    color: #475569;
    line-height: 1.6;
    white-space: pre-wrap;
}
.ro-remaining-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 999px;
    line-height: 1.3;
    white-space: nowrap;
    width: fit-content;
}
.ro-remaining-badge i {
    font-size: 12px;
}
.ro-remaining-badge--safe {
    background: #ecfdf5;
    color: #059669;
    border: 1px solid #a7f3d0;
}
.ro-remaining-badge--warning {
    background: #fffbeb;
    color: #d97706;
    border: 1px solid #fde68a;
}
.ro-remaining-badge--danger {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
}
.ro-remaining-badge--normal {
    background: #f8fafc;
    color: #64748b;
    border: 1px solid #e2e8f0;
}
</style>
